import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MatchNode, RoomMemberSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

interface TournamentBracketProps {
  nodes: MatchNode[];
  members: RoomMemberSummary[];
}

function nameFor(members: RoomMemberSummary[], steamId: string | null): string {
  if (!steamId) return "—";
  return members.find((x) => x.steamId === steamId)?.displayName ?? steamId;
}

function playerRowClass(m: MatchNode, side: "A" | "B"): string {
  const id = side === "A" ? m.playerA : m.playerB;
  const base =
    "rounded-md border px-2.5 py-2 text-sm transition-colors";
  if (!id) {
    return cn(base, "border-dashed border-border/50 bg-muted/15 text-muted-foreground");
  }
  if (!m.winner) {
    return cn(base, "border-border/60 bg-muted/25");
  }
  if (m.winner === id) {
    return cn(
      base,
      "border-emerald-600 bg-emerald-950/50 font-semibold text-emerald-50 ring-1 ring-emerald-500/40",
    );
  }
  return cn(
    base,
    "border-border/30 bg-muted/10 text-muted-foreground opacity-80",
  );
}

export function TournamentBracket({ nodes, members }: TournamentBracketProps) {
  const maxRound = Math.max(0, ...nodes.map((n) => n.round));
  const rounds: MatchNode[][] = [];
  for (let r = 0; r <= maxRound; r++) {
    rounds.push(
      nodes
        .filter((n) => n.round === r)
        .sort((a, b) => a.index - b.index),
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Bracket</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto pb-1">
          <div className="flex min-w-min items-stretch">
            {rounds.map((matches, ri) => (
              <div
                key={ri}
                className="flex min-w-[148px] flex-1 flex-col border-l border-border/50 py-2 pl-4 first:border-l-0 first:pl-0"
              >
                <p className="mb-4 shrink-0 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {ri === maxRound ? "Final" : `Round ${ri + 1}`}
                </p>
                <div className="flex flex-1 flex-col justify-around gap-8">
                  {matches.map((m) => (
                    <div
                      key={m.id}
                      className="flex flex-col gap-1.5 rounded-lg border border-border/40 bg-card/40 p-2 shadow-sm"
                    >
                      <div className={playerRowClass(m, "A")}>{nameFor(members, m.playerA)}</div>
                      <div className="text-center text-[10px] font-medium uppercase text-muted-foreground">
                        vs
                      </div>
                      <div className={playerRowClass(m, "B")}>{nameFor(members, m.playerB)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
